<template>
  <div ref="subscription" class="content-wrapper view-subscription pt-2">
    <div class="content">
      <b-card>
        <h4><i class="fas fa-credit-card"></i> การสมัครบริการ</h4>
        <hr />

        <div>
          <!-- Nav tabs with only two main tabs -->
          <b-tabs content-class="mt-3" pills card>
            <!-- Payment information tab -->
            <b-tab title="ข้อมูลการชำระเงิน" active>
              <b-card-text>
                <!-- Credit and Payment Section -->
                <b-form-group
                  label="ข้อมูลการชำระเงิน"
                  label-cols-lg="3"
                  label-size="lg"
                  label-class="pt-0"
                >
                  <b-row class="justify-content-center">
                    <b-col lg="10">
                      <b-card class="payment-info-card shadow-sm">
                        <div
                          class="payment-header d-flex align-items-center mb-4"
                        >
                          <div class="payment-icon mr-3">
                            <i class="fas fa-wallet"></i>
                          </div>
                          <div>
                            <h5 class="mb-1">ข้อมูลการชำระเงินของคุณ</h5>
                            <p class="text-muted mb-0">
                              ยอดเครดิตสำหรับการสมัครบริการ
                            </p>
                          </div>
                        </div>

                        <div class="payment-details mb-4">
                          <div
                            class="payment-item d-flex justify-content-between align-items-center p-2 mb-2"
                          >
                            <span class="payment-label"
                              ><i class="fas fa-coins mr-2"></i
                              >ยอดเครดิตคงเหลือ:</span
                            >
                            <span
                              class="payment-value font-weight-bold credit-balance"
                              >{{ creditBalance }}</span
                            >
                          </div>
                        </div>

                        <div class="d-flex justify-content-center">
                          <b-button
                            variant="success"
                            @click="openKtbFastPay"
                            class="ktb-button px-4 mr-2"
                            :disabled="isProductionEnvironment"
                          >
                            <i class="fas fa-credit-card mr-2"></i>เติมเงิน
                          </b-button>
                          <b-button
                            variant="outline-primary"
                            @click="togglePaymentHistory"
                            class="view-history-button px-4"
                          >
                            <i class="fas fa-history mr-2"></i
                            >{{
                              showPaymentHistory
                                ? "ซ่อนประวัติการชำระเงิน"
                                : "ดูประวัติการชำระเงิน"
                            }}
                          </b-button>
                        </div>
                      </b-card>
                    </b-col>
                  </b-row>
                </b-form-group>

                <!-- Payment History Section (Lazy Loaded) -->
                <b-collapse v-model="showPaymentHistory">
                  <keep-alive>
                    <component
                      v-if="PaymentHistoryComponent"
                      :is="PaymentHistoryComponent"
                      ref="paymentHistoryComponent"
                    />
                    <div v-else class="text-center py-5">
                      <b-spinner
                        label="กำลังโหลด..."
                        variant="primary"
                      ></b-spinner>
                      <p class="mt-3">กำลังโหลดข้อมูลประวัติการชำระเงิน</p>
                    </div>
                  </keep-alive>
                </b-collapse>
              </b-card-text>
            </b-tab>

            <!-- Subscription and storage information tab -->
            <b-tab title="ข้อมูลพื้นที่จัดเก็บและการสมัคร">
              <b-card-text>
                <!-- Subscription Form Section -->
                <b-form-group
                  label="สมัครแพ็คเกจเพิ่มเติม"
                  label-cols-lg="3"
                  label-size="lg"
                  label-class="pt-0"
                >
                  <!-- Stylish Subscription Form -->
                  <b-row class="justify-content-center">
                    <b-col lg="10">
                      <b-card class="subscription-card shadow-sm">
                        <div class="text-center mb-4">
                          <div class="subscription-icon mb-3">
                            <i class="fas fa-cloud-upload-alt"></i>
                          </div>
                          <h4 class="mb-3">เพิ่มพื้นที่จัดเก็บของคุณ</h4>
                          <p class="text-muted">
                            เลือกแพ็คเกจที่เหมาะกับความต้องการของคุณ
                          </p>
                          <div class="free-storage-alert mt-2">
                            <b-alert
                              v-if="hasExistingSubscription"
                              show
                              variant="warning"
                              class="text-center py-2 mt-2"
                            >
                              <i class="fas fa-info-circle mr-2"></i
                              >คุณมีแพ็คเกจที่ใช้งานอยู่
                              การซื้อพื้นที่เพิ่มจะไม่เปลี่ยนระยะเวลาการใช้งาน
                              <br />
                              <small
                                >วันหมดอายุยังคงเป็น
                                {{ fileStoreExpiration }} เหมือนเดิม</small
                              >
                            </b-alert>
                          </div>
                        </div>

                        <b-form @submit.prevent="submitSubscription">
                          <!-- Package Cards Slider -->
                          <div class="package-slider-container mb-4">
                            <div class="package-slider">
                              <div class="package-cards">
                                <div
                                  v-for="pkg in packages"
                                  :key="pkg.id"
                                  :class="['package-card', { selected: selectedPackageId === pkg.id }]"
                                  @click="selectPackage(pkg.id)"
                                >
                                  <div class="package-badge" v-if="pkg.id === 1">
                                    <span>แนะนำ</span>
                                  </div>
                                  <h5 class="package-name">{{ pkg.name }}</h5>
                                  <div class="package-storage">{{ pkg.storageDisplay }}</div>
                                  <div class="package-price">
                                    ฿{{ pkg.priceBahtPerMonth.toLocaleString() }}
                                    <span class="package-price-period">/เดือน</span>
                                  </div>
                                  <div class="package-selected-indicator" v-if="selectedPackageId === pkg.id">
                                    <i class="fas fa-check-circle"></i>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <!-- Dots indicator -->
                            <div class="package-dots">
                              <span
                                v-for="pkg in packages"
                                :key="'dot-' + pkg.id"
                                :class="['package-dot', { active: selectedPackageId === pkg.id }]"
                                @click="selectPackage(pkg.id)"
                              ></span>
                            </div>
                          </div>

                          <!-- Duration Selector -->
                          <b-row class="justify-content-center" v-if="!hasExistingSubscription">
                            <b-col md="6">
                              <b-form-group
                                label="ระยะเวลา"
                                label-class="font-weight-bold"
                              >
                                <b-form-select
                                  v-model="subscription.durationMonths"
                                  :options="durationOptions"
                                  class="custom-select-lg"
                                  required
                                ></b-form-select>
                                <small class="form-text text-muted"
                                  >เลือกระยะเวลาที่ต้องการสมัคร</small
                                >
                              </b-form-group>
                            </b-col>
                          </b-row>

                          <!-- Price Summary -->
                          <div
                            class="price-display text-center my-4"
                            v-if="!hasExistingSubscription"
                          >
                            <div class="price-amount">
                              ฿{{ calculateTotalPrice().toLocaleString() }}
                            </div>
                            <div class="price-period">
                              {{ getSelectedPackage().name }} ({{ getSelectedPackage().storageDisplay }}) × {{ subscription.durationMonths }} เดือน
                            </div>
                          </div>

                          <div
                            class="price-display text-center my-4 checked-price"
                            v-if="checkedPrice && hasExistingSubscription"
                          >
                            <div class="price-amount">
                              ฿{{ calculatedPriceBaht.toLocaleString() }}
                            </div>
                            <div class="price-title mb-2">
                              <i
                                class="fas fa-check-circle text-success mr-1"
                              ></i>
                              ราคาที่คำนวณแล้ว
                            </div>
                            <div class="price-period">
                              {{ getSelectedPackage().name }} ({{ getSelectedPackage().storageDisplay }})
                              (วันหมดอายุคงเดิม {{ fileStoreExpiration }})
                            </div>
                          </div>

                          <div
                            class="subscription-features p-3 mb-4 rounded"
                            v-if="!hasExistingSubscription"
                          >
                            <h5 class="mb-3">
                              <i
                                class="fas fa-check-circle text-success mr-2"
                              ></i
                              >สิ่งที่คุณจะได้รับ
                            </h5>

                            <!-- Section 1: พื้นที่จัดเก็บ -->
                            <div class="feature-section mb-3">
                              <h6 class="feature-section-title mb-2">
                                <i class="fas fa-hdd mr-2"></i>พื้นที่จัดเก็บ
                              </h6>
                              <div
                                class="feature-item d-flex align-items-center mb-2"
                              >
                                <i class="fas fa-check text-success mr-2"></i>
                                <span
                                  >พื้นที่จัดเก็บทั้งหมด
                                  {{ getSelectedPackage().storageDisplay }}
                                </span>
                              </div>
                              <div
                                class="feature-item d-flex align-items-center mb-2"
                              >
                                <i class="fas fa-check text-success mr-2"></i>
                                <span
                                  >ใช้งานได้
                                  {{ subscription.durationMonths }} เดือน</span
                                >
                              </div>
                              <div
                                class="feature-item d-flex align-items-center mb-2"
                              >
                                <i class="fas fa-check text-success mr-2"></i>
                                <span>จัดเก็บรูป เอกสารและ file DICOM</span>
                              </div>
                            </div>

                            <!-- Section 2: เชื่อมต่อ LINE OA -->
                            <div class="feature-section">
                              <h6 class="feature-section-title mb-2 d-flex align-items-center">
                                <img
                                  src="https://portal.dentcare.app/assets/dentcare_logo_textlow-IySLmGcP.png"
                                  alt="DentCare Logo"
                                  class="dentcare-section-logo mr-2"
                                />
                                เชื่อมต่อ LINE OA
                              </h6>
                              <div
                                class="feature-item d-flex align-items-center mb-2"
                              >
                                <i class="fas fa-check text-success mr-2"></i>
                                <span>เชื่อม LINE OA ของคลินิก กับ DentCare เพื่อส่งข้อความอัตโนมัติ</span>
                              </div>
                              <div
                                class="feature-item d-flex align-items-center mb-2"
                              >
                                <i class="fas fa-check text-success mr-2"></i>
                                <span>สามารถเตือนนัดหมายอัตโนมัติ</span>
                              </div>
                              <div
                                class="feature-item d-flex align-items-center mb-2"
                              >
                                <i class="fas fa-check text-success mr-2"></i>
                                <span>คนไข้ได้รับใบเสร็จทุกครั้งที่รักษาเสร็จ</span>
                              </div>
                              <div
                                class="feature-item d-flex align-items-center mb-2"
                              >
                                <i class="fas fa-check text-success mr-2"></i>
                                <span>คนไข้สามารถประเมินบริการได้</span>
                              </div>
                            </div>
                          </div>

                          <div class="text-center mt-4">
                            <b-button
                              v-if="
                                !hasExistingSubscription ||
                                (hasExistingSubscription && checkedPrice)
                              "
                              type="submit"
                              variant="primary"
                              class="px-4"
                            >
                              <i class="fas fa-cloud-upload-alt mr-2"></i
                              >{{
                                hasExistingSubscription && checkedPrice
                                  ? "ยืนยันการสมัคร"
                                  : "สมัครแพ็คเกจ"
                              }}
                            </b-button>
                            <b-button
                              v-if="hasExistingSubscription && !checkedPrice"
                              type="button"
                              variant="info"
                              class="px-4"
                              @click="checkSubscriptionPrice"
                            >
                              <i class="fas fa-calculator mr-2"></i>ตรวจสอบราคา
                            </b-button>
                          </div>
                        </b-form>
                      </b-card>
                    </b-col>
                  </b-row>
                </b-form-group>

                <hr />

                <!-- Storage Information Section moved to this tab -->
                <b-form-group
                  label="พื้นที่จัดเก็บปัจจุบัน"
                  label-cols-lg="3"
                  label-size="lg"
                  label-class="pt-0"
                >
                  <b-row class="justify-content-center">
                    <b-col lg="10">
                      <b-card class="storage-info-card shadow-sm">
                        <div
                          class="storage-header d-flex align-items-center mb-4"
                        >
                          <div class="storage-icon mr-3">
                            <i class="fas fa-database"></i>
                          </div>
                          <div>
                            <h5 class="mb-1">ข้อมูลการใช้งานพื้นที่จัดเก็บ</h5>
                            <p class="text-muted mb-0">
                              ดูรายละเอียดการใช้งานพื้นที่จัดเก็บของคุณ
                            </p>
                          </div>
                        </div>

                        <div class="storage-details">
                          <div
                            class="storage-item d-flex justify-content-between align-items-center p-2 mb-2 border-bottom"
                          >
                            <span class="storage-label"
                              ><i class="far fa-calendar-alt mr-2"></i
                              >วันหมดอายุ:</span
                            >
                            <span class="storage-value font-weight-bold">{{
                              fileStoreExpiration
                            }}</span>
                          </div>
                          <div
                            class="storage-item storage-total-item d-flex justify-content-between align-items-center p-2 mb-2 border-bottom"
                          >
                            <span class="storage-label"
                              ><i class="fas fa-hdd mr-2"></i>พื้นที่ทั้งหมด:
                              <span class="free-storage-badge"
                                ><i class="fas fa-gift free-gift-icon"></i> รวม
                                1GB ฟรี</span
                              ></span
                            >
                            <span class="storage-value font-weight-bold"
                              >{{ fileStoreQuota }}
                            </span>
                          </div>
                          <div
                            class="storage-item d-flex justify-content-between align-items-center p-2 mb-2 border-bottom"
                          >
                            <span class="storage-label"
                              ><i class="fas fa-server mr-2"></i
                              >พื้นที่ที่ใช้ไป:</span
                            >
                            <span class="storage-value font-weight-bold">{{
                              fileStoreUsage
                            }}</span>
                          </div>

                          <!-- Storage usage bar -->
                          <div class="storage-usage-container mt-3 mb-2">
                            <div
                              class="d-flex justify-content-between align-items-center mb-1"
                            >
                              <span class="storage-usage-label"
                                >การใช้งานพื้นที่</span
                              >
                              <span class="storage-usage-percent"
                                >{{ usagePercentage }}%</span
                              >
                            </div>
                            <b-progress
                              :value="usagePercentage"
                              :variant="usageVariant"
                              height="1.5rem"
                              animated
                              class="storage-usage-bar"
                              show-progress
                            ></b-progress>
                          </div>
                        </div>
                      </b-card>
                    </b-col>
                  </b-row>
                </b-form-group>

                <!-- Subscription History Section (Collapsible) -->
                <b-form-group
                  label="ประวัติการสมัครบริการ"
                  label-cols-lg="3"
                  label-size="lg"
                  label-class="pt-0"
                >
                  <b-row class="justify-content-center">
                    <b-col lg="10">
                      <b-card class="subscription-history-card shadow-sm">
                        <div
                          class="subscription-history-header d-flex align-items-center justify-content-between mb-2"
                        >
                          <div class="d-flex align-items-center">
                            <div class="subscription-history-icon mr-3">
                              <i class="fas fa-history"></i>
                            </div>
                            <div>
                              <h5 class="mb-1">ประวัติการสมัครแพ็คเกจ</h5>
                              <p class="text-muted mb-0">
                                รายการแพ็คเกจทั้งหมดที่คุณมี
                              </p>
                            </div>
                          </div>
                          <b-button
                            v-b-toggle.subscription-history-collapse
                            variant="outline-secondary"
                            size="sm"
                            class="toggle-history-button"
                          >
                            <i
                              class="fas"
                              :class="
                                showSubscriptionHistory
                                  ? 'fa-chevron-up'
                                  : 'fa-chevron-down'
                              "
                            ></i>
                            {{
                              showSubscriptionHistory
                                ? "ซ่อนประวัติ"
                                : "ดูประวัติ"
                            }}
                          </b-button>
                        </div>

                        <!-- Toggle switch for showing all packages -->
                        <div
                          v-if="showSubscriptionHistory"
                          class="subscription-filter-controls d-flex align-items-center justify-content-between mb-3"
                        >
                          <div class="filter-info">
                            <small class="text-muted">
                              <i class="fas fa-filter mr-1"></i>
                              {{
                                showActiveOnly
                                  ? "แสดงเฉพาะแพ็กเกจที่ใช้งานอยู่"
                                  : "แสดงแพ็กเกจทั้งหมด (รวมหมดอายุ)"
                              }}
                            </small>
                          </div>
                          <div class="d-flex align-items-center">
                            <label class="switch-label mr-2">
                              <small class="text-muted">ทั้งหมด</small>
                            </label>
                            <b-form-checkbox
                              v-model="showActiveOnly"
                              switch
                              size="lg"
                              class="subscription-toggle-switch"
                            ></b-form-checkbox>
                            <label class="switch-label ml-2">
                              <small class="text-muted">เฉพาะใช้งานอยู่</small>
                            </label>
                          </div>
                        </div>

                        <b-collapse
                          id="subscription-history-collapse"
                          v-model="showSubscriptionHistory"
                          class="mt-3"
                        >
                          <div
                            v-if="
                              filteredSubscriptionsHistory &&
                              filteredSubscriptionsHistory.length > 0
                            "
                          >
                            <b-table
                              striped
                              hover
                              responsive
                              :items="filteredSubscriptionsHistory"
                              :fields="subscriptionFields"
                              class="subscription-history-table"
                            >
                              <template #cell(storageSizeBytes)="data">
                                {{ bytesToSize(data.item.storageSizeBytes) }}
                              </template>
                              <template #cell(startDate)="data">
                                {{ formatDate(data.item.startDate) }}
                              </template>
                              <template #cell(endDate)="data">
                                {{ formatDate(data.item.endDate) }}
                              </template>
                              <template #cell(type)="data">
                                <b-badge
                                  :variant="
                                    getSubscriptionTypeVariant(data.item.type)
                                  "
                                >
                                  {{ getSubscriptionTypeLabel(data.item.type) }}
                                </b-badge>
                              </template>
                              <template #cell(status)="data">
                                <b-badge
                                  :variant="
                                    data.item.active ? 'success' : 'secondary'
                                  "
                                >
                                  {{
                                    data.item.active ? "ใช้งานอยู่" : "หมดอายุ"
                                  }}
                                </b-badge>
                              </template>
                            </b-table>
                          </div>
                          <div v-else class="text-center py-4">
                            <div class="empty-icon mb-3">
                              <i class="far fa-calendar-times"></i>
                            </div>
                            <p class="text-muted">
                              {{
                                showActiveOnly
                                  ? "ไม่มีแพ็คเกจที่ใช้งานอยู่"
                                  : "ไม่มีประวัติการสมัครแพ็คเกจ"
                              }}
                            </p>
                            <small
                              v-if="
                                showActiveOnly &&
                                subscriptionsHistory.length > 0
                              "
                              class="text-info"
                            >
                              <i class="fas fa-info-circle mr-1"></i>
                              ลองเปิดดูแพ็กเกจทั้งหมดด้วยการปิดสวิตช์ด้านบน
                            </small>
                          </div>
                        </b-collapse>
                      </b-card>
                    </b-col>
                  </b-row>
                </b-form-group>
              </b-card-text>
            </b-tab>
          </b-tabs>
        </div>

        <!-- Placeholder for future subscription-related content -->
      </b-card>
      <Dialog ref="Dialog"></Dialog>
      <Loading v-if="isLoading"></Loading>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import bytesToSize from "@/utils/bytesToSize";
import moment from "moment";

export default {
  name: "Subscription",
  components: {
    Dialog,
    Loading,
  },
  data() {
    return {
      // Loading state
      isLoading: false,

      // Storage information
      fileStoreExpiration: "",
      fileStoreQuota: "",
      fileStoreUsage: "",
      creditBalance: null,
      currentFileStorageSubscription: null,

      // Fixed packages
      packages: [
        { id: 1, name: "Standard", storageSizeGB: 50, storageDisplay: "50 GB", priceBahtPerMonth: 990 },
        { id: 2, name: "Professional", storageSizeGB: 1000, storageDisplay: "1 TB", priceBahtPerMonth: 1490 },
      ],
      selectedPackageId: 1, // default to Standard

      // Subscription form data
      subscription: {
        durationMonths: 1,
      },
      showSubscriptionSummary: true,

      // Dropdown options
      durationOptions: [
        { value: 1, text: "1 เดือน" },
        { value: 3, text: "3 เดือน" },
        { value: 6, text: "6 เดือน" },
        { value: 12, text: "12 เดือน" },
      ],
      calculatedPriceBaht: 0,
      checkedPrice: false,

      // Payment history lazy loading
      showPaymentHistory: false,
      PaymentHistoryComponent: null,

      // Subscription history
      subscriptionFields: [
        { key: "id", label: "รหัส", sortable: true },
        { key: "type", label: "ประเภท", sortable: true },
        { key: "storageSizeBytes", label: "ขนาดพื้นที่", sortable: true },
        { key: "startDate", label: "วันที่เริ่มต้น", sortable: true },
        { key: "endDate", label: "วันที่สิ้นสุด", sortable: true },
        { key: "status", label: "สถานะ", sortable: true },
      ],
      subscriptionsHistory: [],
      showSubscriptionHistory: false,
      showActiveOnly: true, // Default to show only active packages
      bytesToSize,
    };
  },
  computed: {
    ...mapGetters({
      getBranchInfo: "moduleBranch/getBranchInfo",
    }),

    // Environment detection
    isProductionEnvironment() {
      return process.env.VUE_APP_MODE === 'production';
    },

    /**
     * Calculate usage percentage
     * @returns {Number} - Usage percentage (0-100)
     */
    usagePercentage() {
      if (!this.getBranchInfo) return 0;

      const total = this.getBranchInfo.totalFileStorageSizeBytes || 1;
      const used = this.getBranchInfo.fileStoreUsage || 0;

      // Calculate percentage and round to 1 decimal place
      const percentage = Math.round((used / total) * 1000) / 10;

      // Ensure value is between 0 and 100
      return Math.min(Math.max(percentage, 0), 100);
    },

    /**
     * Determine the color variant for the progress bar based on usage
     * @returns {String} - Bootstrap color variant
     */
    usageVariant() {
      if (this.usagePercentage >= 90) {
        return "danger";
      } else if (this.usagePercentage >= 75) {
        return "warning";
      } else if (this.usagePercentage >= 50) {
        return "info";
      } else {
        return "success";
      }
    },

    /**
     * Check if the branch already has an active subscription
     * @returns {Boolean} - True if there's an existing subscription
     */
    hasExistingSubscription() {
      return (
        this.getBranchInfo &&
        this.getBranchInfo.currentFileStorageSubscription !== null
      );
    },

    /**
     * Filter subscriptions based on the toggle state
     * @returns {Array} - Filtered subscription history
     */
    filteredSubscriptionsHistory() {
      if (
        !this.subscriptionsHistory ||
        this.subscriptionsHistory.length === 0
      ) {
        return [];
      }

      if (this.showActiveOnly) {
        // Show only active packages
        return this.subscriptionsHistory.filter(
          subscription => subscription.active
        );
      } else {
        // Show all packages
        return this.subscriptionsHistory;
      }
    },
  },
  watch: {
    "subscription.durationMonths"() {
      this.showSubscriptionSummary = true;
    },
    selectedPackageId() {
      this.showSubscriptionSummary = true;

      // Reset checked price when package changes
      if (this.hasExistingSubscription && this.checkedPrice) {
        this.checkedPrice = false;
        this.calculatedPriceBaht = 0;
        this.showDialogToast(
          "info",
          "กรุณาตรวจสอบราคาใหม่เนื่องจากมีการเปลี่ยนแพ็คเกจ"
        );
      }
    },
    "$route.params": {
      handler(newParams, oldParams) {
        if (
          newParams.branchUrl !== oldParams.branchUrl ||
          newParams.clinicUrl !== oldParams.clinicUrl
        ) {
          this.fetchBranchData();
        }
      },
      deep: true,
    },
  },
  created() {
    this.fetchBranchData();
  },
  methods: {
    ...mapActions({
      subscribeFileStorage: "moduleMediaFile/subscribeFileStorage",
      fetchBranchByUrl: "moduleBranch/fetchBranchByUrl",
    }),

    /**
     * Fetch branch data from API
     */
    async fetchBranchData() {
      const { clinicUrl, branchUrl } = this.$route.params;
      if (!clinicUrl || !branchUrl) {
        this.showDialogToast("danger", "ไม่พบข้อมูลคลินิกหรือสาขา");
        return;
      }

      try {
        this.isLoading = true;
        await this.fetchBranchByUrl({
          clinicUrl,
          branchUrl,
        });
        this.resetConfig();
      } catch (error) {
        console.error("Error fetching branch data:", error);
        this.showDialogToast("danger", "ไม่สามารถดึงข้อมูลคลินิกและสาขาได้");
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Initialize the component
     */
    init() {
      this.resetConfig();
    },

    /**
     * Reset configuration from branch info
     */
    resetConfig() {
      if (!this.getBranchInfo) return;

      // Format expiration date
      this.fileStoreExpiration = this.formatBranchDate(
        this.getBranchInfo.fileStoreExpiration
      );

      // Format storage sizes
      this.fileStoreQuota = this.formatStorageSize(
        this.getBranchInfo.totalFileStorageSizeBytes
      );
      this.fileStoreUsage = this.formatStorageSize(
        this.getBranchInfo.fileStoreUsage
      );

      // Format credit balance
      this.creditBalance = this.formatCreditBalance(
        this.getBranchInfo.creditBalance
      );

      // Set subscription info
      this.currentFileStorageSubscription =
        this.getBranchInfo.currentFileStorageSubscription || "ไม่มีข้อมูล";

      // Set subscription history
      this.setSubscriptionHistory();
    },

    /**
     * Format branch date with Thai locale
     * @param {String} dateString - Date string from API
     * @returns {String} - Formatted date
     */
    formatBranchDate(dateString) {
      return dateString ? moment(dateString).locale("th").format("LL") : "";
    },

    /**
     * Format storage size in bytes to human-readable format
     * @param {Number} bytes - Size in bytes
     * @returns {String} - Formatted size string
     */
    formatStorageSize(bytes) {
      return bytesToSize(bytes || 0);
    },

    /**
     * Format credit balance
     * @param {Number} amount - Credit balance amount
     * @returns {String} - Formatted credit balance
     */
    formatCreditBalance(amount) {
      return amount !== null ? `${amount} บาท` : "ไม่มีข้อมูล";
    },

    /**
     * Set subscription history from branch info
     */
    setSubscriptionHistory() {
      if (
        this.getBranchInfo.fileStorageSubscriptions &&
        Array.isArray(this.getBranchInfo.fileStorageSubscriptions)
      ) {
        this.subscriptionsHistory = [
          ...this.getBranchInfo.fileStorageSubscriptions,
        ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      } else {
        this.subscriptionsHistory = [];
      }
    },

    /**
     * Reset subscription form
     */
    resetForm() {
      this.subscription.durationMonths = 1;
      this.selectedPackageId = 1; // Reset to Standard package
      this.showSubscriptionSummary = false;
      this.checkedPrice = false;
      this.calculatedPriceBaht = 0;
    },

    /**
     * Check subscription price for existing subscriptions
     */
    async checkSubscriptionPrice() {
      const { clinicUrl, branchUrl } = this.$route.params;
      if (!clinicUrl || !branchUrl) {
        this.showDialogToast("danger", "ไม่พบข้อมูลคลินิกหรือสาขา");
        return;
      }

      try {
        this.isLoading = true;

        // Call the API to check price
        const priceCheckResult = await this.subscribeFileStorage({
          clinicUrl,
          branchUrl,
          durationMonths: 1, // Set to 1 for existing subscriptions
          packageId: this.selectedPackageId,
          confirmed: false, // Just checking price
        });

        if (priceCheckResult.status === 200) {
          // Get the calculated price from API response
          this.calculatedPriceBaht =
            priceCheckResult.data.subscriptionPriceBaht || 0;
          this.checkedPrice = true;
        }
      } catch (error) {
        console.error("Error checking subscription price:", error);
        this.showDialogToast(
          "danger",
          `เกิดข้อผิดพลาด: ${
            error.response?.data?.errorMessage || "ไม่สามารถตรวจสอบราคาได้"
          }`
        );
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Select a package
     * @param {Number} packageId - The package ID to select
     */
    selectPackage(packageId) {
      this.selectedPackageId = packageId;
    },

    /**
     * Get the currently selected package
     * @returns {Object} - The selected package object
     */
    getSelectedPackage() {
      return this.packages.find(p => p.id === this.selectedPackageId) || this.packages[0];
    },

    /**
     * Calculate total price based on selected package and duration
     * @returns {Number} - The calculated total price
     */
    calculateTotalPrice() {
      const pkg = this.getSelectedPackage();
      return pkg.priceBahtPerMonth * this.subscription.durationMonths;
    },

    /**
     * Submit the subscription form
     */
    async submitSubscription() {
      const { clinicUrl, branchUrl } = this.$route.params;
      if (!clinicUrl || !branchUrl) {
        this.showDialogToast("danger", "ไม่พบข้อมูลคลินิกหรือสาขา");
        return;
      }

      try {
        // Show loading indicator
        this.isLoading = true;

        // Handle different flows based on subscription status
        if (this.hasExistingSubscription) {
          if (!this.checkedPrice) {
            // If price hasn't been checked yet, do that first
            await this.checkSubscriptionPrice();
            return;
          }

          // Price has been checked, proceed with purchase
          const result = await this.subscribeFileStorage({
            clinicUrl,
            branchUrl,
            durationMonths: 1, // Set to 1 for existing subscriptions
            packageId: this.selectedPackageId,
            confirmed: true, // Confirm the purchase
          });

          if (result.status === 200) {
            // Check if subscription was successful
            if (result.data.newSubscription) {
              this.showDialogToast(
                "success",
                "เพิ่มพื้นที่จัดเก็บเรียบร้อยแล้ว"
              );
              await this.fetchBranchData();

              // Refresh payment history if component is loaded
              if (
                this.showPaymentHistory &&
                this.$refs.paymentHistoryComponent
              ) {
                this.$refs.paymentHistoryComponent.loadPaymentHistory();
              }

              this.resetForm();
            } else if (result.data.reason === "InsufficientCreditBalance") {
              // Handle insufficient credit balance for existing subscription
              this.isLoading = false; // Stop loading before showing dialog

              const requiredAmount =
                result.data.subscriptionPriceBaht || this.calculatedPriceBaht;
              const currentBalance = this.creditBalance
                ? parseInt(this.creditBalance.replace(" บาท", ""))
                : 0;
              const shortfall = requiredAmount - currentBalance;

              const topUpMessage = `เครดิตไม่เพียงพอสำหรับการเพิ่มพื้นที่\n\nต้องการเติมเงินเพื่อเพิ่มพื้นที่นี้หรือไม่?`;

              const shouldTopUp = await this.$refs.Dialog.showAlertConfirm(
                "เครดิตไม่เพียงพอ",
                topUpMessage
              );

              if (shouldTopUp && shouldTopUp.isConfirmed) {
                // Open KTB Fast Pay for top-up
                this.openKtbFastPay();
              }
            } else {
              // Handle other reasons
              this.isLoading = false; // Stop loading before showing error
              this.showDialogToast(
                "warning",
                `ไม่สามารถเพิ่มพื้นที่ได้: ${
                  result.data.reason || "เหตุผลไม่ทราบ"
                }`
              );
            }
          }
        } else {
          // For new subscriptions
          // Make sure the subscription summary is visible
          this.showSubscriptionSummary = true;
          this.isLoading = false;

          // Show confirmation dialog with calculated price
          const selectedPkg = this.getSelectedPackage();
          const confirmMessage = `ยืนยันการสมัครแพ็คเกจ ${selectedPkg.name} (${selectedPkg.storageDisplay}) เป็นระยะเวลา ${
            this.subscription.durationMonths
          } เดือน ในราคา ${this.calculateTotalPrice().toLocaleString()} บาท`;

          const isConfirmed = await this.$refs.Dialog.showAlertConfirm(
            "ยืนยันการสมัคร",
            confirmMessage
          );

          if (isConfirmed && isConfirmed.isConfirmed) {
            // Show loading again
            this.isLoading = true;

            // Complete the purchase directly (no price check needed)
            const result = await this.subscribeFileStorage({
              clinicUrl,
              branchUrl,
              durationMonths: this.subscription.durationMonths,
              packageId: this.selectedPackageId,
              confirmed: true, // Directly confirm the purchase
            });

            if (result.status === 200) {
              // Check if subscription was successful
              if (result.data.newSubscription) {
                this.showDialogToast("success", "สมัครแพ็คเกจเรียบร้อยแล้ว");
                await this.fetchBranchData();

                // Refresh payment history if component is loaded
                if (
                  this.showPaymentHistory &&
                  this.$refs.paymentHistoryComponent
                ) {
                  this.$refs.paymentHistoryComponent.loadPaymentHistory();
                }

                this.resetForm();
              } else if (result.data.reason === "InsufficientCreditBalance") {
                // Handle insufficient credit balance
                this.isLoading = false; // Stop loading before showing dialog

                const requiredAmount =
                  result.data.subscriptionPriceBaht || this.calculateTotalPrice();
                const currentBalance = this.creditBalance
                  ? parseInt(this.creditBalance.replace(" บาท", ""))
                  : 0;
                const shortfall = requiredAmount - currentBalance;

                const topUpMessage = `เครดิตไม่เพียงพอสำหรับการสมัครแพ็คเกจ\n\nต้องการเติมเงินเพื่อสมัครแพ็คเกจนี้หรือไม่?`;

                const shouldTopUp = await this.$refs.Dialog.showAlertConfirm(
                  "เครดิตไม่เพียงพอ",
                  topUpMessage
                );

                if (shouldTopUp && shouldTopUp.isConfirmed) {
                  // Open KTB Fast Pay for top-up
                  this.openKtbFastPay();
                }
              } else {
                // Handle other reasons
                this.isLoading = false; // Stop loading before showing error
                this.showDialogToast(
                  "warning",
                  `ไม่สามารถสมัครได้: ${result.data.reason || "เหตุผลไม่ทราบ"}`
                );
              }
            }
          }
        }
      } catch (error) {
        console.error("Error with subscription process:", error);
        this.showDialogToast(
          "danger",
          `เกิดข้อผิดพลาด: ${
            error.response?.data?.errorMessage || "ไม่สามารถดำเนินการได้"
          }`
        );
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Show dialog toast
     * @param {String} variant - Toast variant (success, danger, etc.)
     * @param {String} textDetail - Toast message
     */
    showDialogToast(variant, textDetail) {
      this.$refs.Dialog.showToast(variant, textDetail);
    },

    /**
     * Open KTB Fast Pay in a new tab
     */
    openKtbFastPay() {
      const { clinicUrl, branchUrl } = this.$route.params;
      if (!clinicUrl || !branchUrl) {
        this.$refs.Dialog.showToast("error", "ไม่พบข้อมูลคลินิกหรือสาขา");
        return;
      }

      // Build the full URL including domain
      const baseUrl = window.location.origin;
      const path = `${baseUrl}/${clinicUrl}/${branchUrl}/ktbfastpay`;

      // Open in new tab
      window.open(path, "_blank");
    },

    /**
     * Toggle payment history visibility and load the component if needed
     */
    togglePaymentHistory() {
      // Toggle the visibility state
      this.showPaymentHistory = !this.showPaymentHistory;

      // If we're showing the history and the component hasn't been loaded yet
      if (this.showPaymentHistory && !this.PaymentHistoryComponent) {
        // Dynamically import the component only when needed
        import("@/components/subscription/PaymentHistory.vue")
          .then(module => {
            this.PaymentHistoryComponent = module.default;

            // Initialize the component after it's loaded
            this.$nextTick(() => {
              if (this.$refs.paymentHistoryComponent) {
                this.$refs.paymentHistoryComponent.initialize();
              }
            });
          })
          .catch(error => {
            console.error("Error loading payment history component:", error);
            this.showDialogToast(
              "danger",
              "ไม่สามารถโหลดข้อมูลประวัติการชำระเงินได้"
            );
            this.showPaymentHistory = false;
          });
      }
    },

    /**
     * Refresh all subscription data
     */
    refreshSubscriptionData() {
      this.fetchBranchData();

      // Also refresh payment history if loaded
      if (this.showPaymentHistory && this.$refs.paymentHistoryComponent) {
        this.$refs.paymentHistoryComponent.loadPaymentHistory();
      }
    },

    /**
     * Format date to Thai locale
     * @param {String} dateString - Date string
     * @returns {String} - Formatted date
     */
    formatDate(dateString) {
      if (!dateString) return "-";
      return moment(dateString).locale("th").format("LL");
    },

    /**
     * Get badge variant for subscription type
     * @param {String} type - Subscription type
     * @returns {String} - Badge variant
     */
    getSubscriptionTypeVariant(type) {
      const variants = {
        Activation: "primary",
        Extension: "info",
        Renewal: "success",
        default: "secondary",
      };
      return variants[type] || variants.default;
    },

    /**
     * Get translated label for subscription type
     * @param {String} type - Subscription type
     * @returns {String} - Translated label
     */
    getSubscriptionTypeLabel(type) {
      const labels = {
        Activation: "เปิดใช้งาน",
        Extension: "ขยายพื้นที่",
        Renewal: "ต่ออายุ",
        default: "อื่นๆ",
      };
      return labels[type] || labels.default;
    },
  },
};
</script>

<style scoped>
/* General styling */
.b-form-group {
  margin-bottom: 1.5rem;
}

h4 {
  color: #3490dc;
}

.form-control:disabled {
  background-color: #f8f9fa;
  border-color: #e9ecef;
  color: #495057;
}

.form-control[disabled] {
  font-weight: 500;
}

.btn {
  transition: all 0.3s ease;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Animation keyframes */
@keyframes floating {
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.6;
  }
}

/* Subscription Card Styling */
.subscription-card {
  border: none;
  border-radius: 1rem;
  overflow: hidden;
  transition: all 0.3s ease;
}

.subscription-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
}

.free-storage-alert .alert {
  border-left: 4px solid #17a2b8;
  background-color: rgba(23, 162, 184, 0.1);
  color: #17a2b8;
  font-weight: 500;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.free-storage-alert .alert-warning {
  border-left: 4px solid #ffc107;
  background-color: rgba(255, 193, 7, 0.1);
  color: #856404;
}

/* Package Slider Styles */
.package-slider-container {
  margin-bottom: 1.5rem;
}

.package-slider {
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #3490dc #f8f9fa;
}

.package-slider::-webkit-scrollbar {
  height: 6px;
}

.package-slider::-webkit-scrollbar-track {
  background: #f8f9fa;
  border-radius: 3px;
}

.package-slider::-webkit-scrollbar-thumb {
  background: #3490dc;
  border-radius: 3px;
}

.package-cards {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  justify-content: center;
}

.package-card {
  flex: 0 0 200px;
  scroll-snap-align: start;
  border: 2px solid #e9ecef;
  border-radius: 1rem;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  position: relative;
  background: #fff;
}

.package-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.package-card.selected {
  border-color: #3490dc;
  background: rgba(52, 144, 220, 0.05);
  box-shadow: 0 4px 12px rgba(52, 144, 220, 0.2);
}

.package-badge {
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.package-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0.5rem;
  margin-top: 0.5rem;
}

.package-storage {
  font-size: 2rem;
  font-weight: 700;
  color: #3490dc;
  margin-bottom: 0.5rem;
}

.package-price {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
}

.package-price-period {
  font-size: 0.85rem;
  font-weight: 400;
  color: #6c757d;
}

.package-selected-indicator {
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  color: #3490dc;
  font-size: 1.5rem;
}

.package-dots {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
}

.package-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #dee2e6;
  cursor: pointer;
  transition: all 0.3s ease;
}

.package-dot.active {
  background: #3490dc;
  transform: scale(1.2);
}

.package-dot:hover {
  background: #6c757d;
}

.subscription-icon {
  font-size: 4rem;
  color: #3490dc;
  margin: 1rem 0;
  animation: floating 3s ease-in-out infinite;
}

.price-display {
  background-color: #f8f9fa;
  border-radius: 1rem;
  padding: 1.5rem;
  transition: all 0.3s ease;
  animation: fadeIn 0.5s ease;
}

.price-display.checked-price {
  border: 2px solid #28a745;
  background-color: rgba(40, 167, 69, 0.05);
}

.price-amount {
  font-size: 2.5rem;
  font-weight: bold;
  color: #3490dc;
  margin-bottom: 0.5rem;
}

.price-title {
  font-size: 1rem;
  color: #28a745;
  font-weight: 500;
}

.price-period {
  color: #6c757d;
  font-size: 1rem;
}

.subscription-features {
  background-color: rgba(52, 144, 220, 0.05);
  border-left: 4px solid #3490dc;
  animation: fadeIn 0.5s ease 0.2s both;
}

.feature-item {
  padding: 0.5rem;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.feature-item:hover {
  background-color: rgba(52, 144, 220, 0.1);
}

.dentcare-feature-logo {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.feature-section {
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid rgba(52, 144, 220, 0.1);
}

.feature-section-title {
  font-weight: 600;
  color: #3490dc;
  font-size: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(52, 144, 220, 0.2);
}

.dentcare-section-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.custom-select-lg {
  height: 3rem;
  font-size: 1.1rem;
  border-radius: 0.5rem;
  padding-left: 1rem;
  border-color: #ddd;
  transition: all 0.3s ease;
}

.custom-select-lg:focus {
  border-color: #3490dc;
  box-shadow: 0 0 0 0.2rem rgba(52, 144, 220, 0.25);
}

/* Storage Info Card Styling */
.storage-info-card {
  border: none;
  border-radius: 1rem;
  background-color: #f8f9fa;
}

.storage-icon {
  font-size: 2.5rem;
  color: #6c757d;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(108, 117, 125, 0.1);
  border-radius: 50%;
}

.storage-item {
  transition: all 0.2s ease;
  border-radius: 0.5rem;
}

.storage-item:hover {
  background-color: rgba(108, 117, 125, 0.05);
}

.storage-total-item {
  background-color: rgba(40, 167, 69, 0.05);
  border-left: 3px solid #28a745;
}

.storage-total-item:hover {
  background-color: rgba(40, 167, 69, 0.08);
}

.storage-label {
  color: #6c757d;
}

.storage-value {
  color: #343a40;
}

.free-storage-badge {
  font-size: 0.85rem;
  color: #28a745;
  font-weight: bold;
  background-color: rgba(40, 167, 69, 0.1);
  padding: 0.2rem 0.6rem;
  border-radius: 1rem;
  margin-left: 0.5rem;
  border: 1px dashed #28a745;
}

.free-gift-icon {
  color: #28a745;
  margin-right: 0.15rem;
  font-size: 0.8rem;
}

.free-plus-icon {
  color: #28a745;
  font-size: 0.85rem;
  margin: 0 0.15rem;
}

.free-package-note {
  font-size: 0.9rem;
  color: #28a745;
  font-style: italic;
}

/* Storage Usage Bar Styling */
.storage-usage-container {
  padding: 0.5rem;
  background-color: rgba(108, 117, 125, 0.05);
  border-radius: 0.5rem;
  transition: all 0.2s ease;
}

.storage-usage-container:hover {
  background-color: rgba(108, 117, 125, 0.08);
}

.storage-usage-label {
  font-weight: 500;
  color: #6c757d;
}

.storage-usage-percent {
  font-weight: bold;
}

.storage-usage-bar {
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

/* Payment Info Card Styling */
.payment-info-card {
  border: none;
  border-radius: 1rem;
  background: linear-gradient(145deg, #ffffff, #f8f9fa);
  overflow: hidden;
  transition: all 0.3s ease;
}

.payment-info-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
}

.payment-icon {
  font-size: 2.5rem;
  color: #28a745;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(40, 167, 69, 0.1);
  border-radius: 50%;
  animation: pulse 2s infinite;
}

.payment-item {
  transition: all 0.2s ease;
  border-radius: 0.5rem;
}

.payment-item:hover {
  background-color: rgba(40, 167, 69, 0.05);
}

.payment-label {
  color: #6c757d;
}

.payment-value {
  color: #343a40;
}

.credit-balance {
  color: #28a745;
  font-size: 1.2rem;
}

.ktb-button {
  transition: all 0.3s ease;
  border-radius: 50px;
  padding: 0.5rem 2rem;
  box-shadow: 0 4px 6px rgba(40, 167, 69, 0.1);
  border: none;
}

.ktb-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(40, 167, 69, 0.2);
}

.ktb-button:active {
  transform: translateY(1px);
}

/* Payment History Card Styling */
.payment-history-card {
  border: none;
  border-radius: 1rem;
  background: linear-gradient(145deg, #ffffff, #f8f9fa);
  overflow: hidden;
  transition: all 0.3s ease;
}

.payment-history-icon {
  font-size: 2.5rem;
  color: #6c757d;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(108, 117, 125, 0.1);
  border-radius: 50%;
}

.payment-history-table {
  margin-top: 1rem;
}

.payment-history-table th {
  font-weight: 600;
  background-color: rgba(52, 144, 220, 0.05);
  border-bottom: 2px solid #3490dc;
}

.payment-history-table td {
  vertical-align: middle;
}

.payment-history-empty .empty-icon {
  font-size: 4rem;
  color: #dee2e6;
  animation: pulse 3s infinite;
}

.view-history-button {
  transition: all 0.3s ease;
  border-radius: 50px;
  padding: 0.5rem 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.view-history-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
}

.view-history-button:active {
  transform: translateY(1px);
}

/* Subscription History Card Styling */
.subscription-history-card {
  border: none;
  border-radius: 1rem;
  background-color: #f8f9fa;
  overflow: hidden;
  transition: all 0.3s ease;
}

.subscription-history-card:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08) !important;
}

.subscription-history-icon {
  font-size: 2.5rem;
  color: #3490dc;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(52, 144, 220, 0.1);
  border-radius: 50%;
}

.subscription-history-table {
  margin-top: 1rem;
}

.subscription-history-table th {
  font-weight: 600;
  background-color: rgba(52, 144, 220, 0.05);
  border-bottom: 2px solid #3490dc;
}

.subscription-history-table td {
  vertical-align: middle;
}

.toggle-history-button {
  transition: all 0.3s ease;
  border-radius: 20px;
  padding: 0.25rem 0.75rem;
  border-color: #ddd;
}

.toggle-history-button:hover {
  background-color: #f8f9fa;
  transform: translateY(-1px);
}

.toggle-history-button i {
  margin-right: 0.5rem;
  transition: all 0.3s ease;
}

.empty-icon {
  font-size: 3.5rem;
  color: #dee2e6;
  animation: pulse 3s infinite;
}

/* Subscription Filter Controls Styling */
.subscription-filter-controls {
  padding: 0.75rem 1rem;
  background-color: rgba(52, 144, 220, 0.03);
  border-radius: 0.5rem;
  border: 1px solid rgba(52, 144, 220, 0.1);
  margin-bottom: 1rem;
}

.filter-info {
  display: flex;
  align-items: center;
}

.switch-label {
  font-weight: 500;
  margin-bottom: 0;
  white-space: nowrap;
}

.subscription-toggle-switch .custom-control-label {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.subscription-toggle-switch
  .custom-switch
  .custom-control-input:checked
  ~ .custom-control-label::before {
  background-color: #3490dc;
  border-color: #3490dc;
}

.subscription-toggle-switch
  .custom-switch
  .custom-control-input:focus
  ~ .custom-control-label::before {
  box-shadow: 0 0 0 0.2rem rgba(52, 144, 220, 0.25);
  border-color: #80bdff;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .price-amount {
    font-size: 2rem;
  }

  .subscription-icon,
  .payment-icon,
  .payment-history-icon {
    font-size: 2rem;
  }

  .storage-icon,
  .payment-icon,
  .payment-history-icon {
    font-size: 2rem;
    width: 50px;
    height: 50px;
  }

  .payment-history-table {
    font-size: 0.9rem;
  }

  .subscription-filter-controls {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .subscription-filter-controls .d-flex {
    margin-top: 0.5rem;
    align-self: center;
  }

  .filter-info {
    margin-bottom: 0.5rem;
  }

  /* Package cards responsive */
  .package-cards {
    justify-content: flex-start;
    padding: 0.5rem;
  }

  .package-card {
    flex: 0 0 160px;
    padding: 1rem;
  }

  .package-storage {
    font-size: 1.5rem;
  }

  .package-name {
    font-size: 1rem;
  }

  .package-price {
    font-size: 0.95rem;
  }
}
</style>